let subscribers = {};
let debouncedEvents = {};
let batchedEvents = {};

// Performance optimizations
const eventMetrics = {
  published: 0,
  batched: 0,
  debounced: 0
};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) {
    subscribers[eventName] = [];
  }

  subscribers[eventName] = [...subscribers[eventName], callback];

  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => {
      return cb !== callback;
    });
  };
}

function publish(eventName, data) {
  eventMetrics.published++;
  
  // Debounce high-frequency events
  if (shouldDebounceEvent(eventName)) {
    debounceEvent(eventName, data);
    return;
  }
  
  // Batch related events
  if (shouldBatchEvent(eventName)) {
    batchEvent(eventName, data);
    return;
  }
  
  // Execute immediately for critical events
  executeEvent(eventName, data);
}

function shouldDebounceEvent(eventName) {
  const debouncedEventTypes = ['quantity-update', 'variant-change'];
  return debouncedEventTypes.includes(eventName);
}

function shouldBatchEvent(eventName) {
  const batchedEventTypes = ['cart-update'];
  return batchedEventTypes.includes(eventName);
}

function debounceEvent(eventName, data) {
  const debounceDelay = 150; // ms
  
  if (debouncedEvents[eventName]) {
    clearTimeout(debouncedEvents[eventName].timeout);
  }
  
  debouncedEvents[eventName] = {
    data,
    timeout: setTimeout(() => {
      executeEvent(eventName, debouncedEvents[eventName].data);
      delete debouncedEvents[eventName];
      eventMetrics.debounced++;
    }, debounceDelay)
  };
}

function batchEvent(eventName, data) {
  const batchDelay = 50; // ms
  
  if (!batchedEvents[eventName]) {
    batchedEvents[eventName] = {
      events: [],
      timeout: setTimeout(() => {
        const batch = batchedEvents[eventName];
        
        // Execute with batched data
        executeEvent(eventName, {
          ...data,
          batch: batch.events,
          batchSize: batch.events.length
        });
        
        delete batchedEvents[eventName];
        eventMetrics.batched += batch.events.length;
      }, batchDelay)
    };
  }
  
  batchedEvents[eventName].events.push(data);
}

function executeEvent(eventName, data) {
  if (subscribers[eventName]) {
    try {
      subscribers[eventName].forEach((callback) => {
        // Use requestIdleCallback for non-critical callbacks
        if (window.requestIdleCallback && !isCriticalEvent(eventName)) {
          window.requestIdleCallback(() => callback(data));
        } else {
          callback(data);
        }
      });
    } catch (error) {
      console.error(`Error executing event ${eventName}:`, error);
    }
  }
  
  // Automatically invalidate section cache for cart-related events
  if ((eventName === 'cart-update' || eventName === 'cart-error') && window.cartSectionManager) {
    window.cartSectionManager.invalidateCache();
  }
}

function isCriticalEvent(eventName) {
  const criticalEvents = ['cart-error', 'cart-update'];
  return criticalEvents.includes(eventName);
}

// Enhanced publish function with priority
function publishPriority(eventName, data, priority = 'normal') {
  if (priority === 'high') {
    executeEvent(eventName, data);
  } else {
    publish(eventName, data);
  }
}

// Performance monitoring
function getPubSubMetrics() {
  return {
    ...eventMetrics,
    activeSubscribers: Object.keys(subscribers).length,
    pendingDebounced: Object.keys(debouncedEvents).length,
    pendingBatched: Object.keys(batchedEvents).length
  };
}

// Debug utilities
if (window.cartPerformanceDebug) {
  window.pubSubMetrics = getPubSubMetrics;
}
